import React, { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Drawer,
  Hidden,
  List,
  ListSubheader
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  CirclePlus as ControlPointIcon,
  LayoutDashboard as DashboardIcon,
  Building2 as BusinessIcon,
  BriefcaseBusiness as WorkOutlineOutlinedIcon,
  Mail as MailOutlineOutlinedIcon,
  ShieldAlert as AssignmentLateOutlinedIcon,
  Truck as PollOutlinedIcon,
  ContactRound as ContactsOutlinedIcon,
  LogOut as ExitToAppIcon,
  CalendarDays as CalendarIcon,
  ClipboardList as ClipboardIcon,
  FileText as FileTextIcon,
  WalletCards as ServerIcon,
  UsersRound as UsersIcon,
  Bot as BotIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import screenRoutes from 'src/config/screen.routes';
import { includesAny } from 'src/constants';
import { logout } from 'src/store/actions/accountActions';
import NavItem from './NavItem';

const navConfig = [
  {
    subheader: 'Management',
    roleAccess: [
      'SuperUser',
      'Admin',
      'LoanServicer',
      'CustomerService',
      'AccountingDept'
    ],
    items: [
      {
        title: 'dashboard',
        roleAccess: ['SuperUser', 'Admin'],
        icon: DashboardIcon,
        href: screenRoutes.management.dashboard
      },
      {
        title: 'portfolioLoans',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        icon: ServerIcon,
        items: [
          {
            title: 'searchLoans',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.loans.list
          },

          /*
           * {
           *   title: 'portfolioPerformance',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService',
           *     'AccountingDept',
           *   ],
           *   href: `${screenRoutes.portfolioPerformance.index}`,
           * },
           */
          {
            title: 'activeLoans',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.reports.activeLoans
          },
          {
            title: 'delinquentLoans',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.reports.delinquentLoans
          },

          {
            title: 'delinquencyNoticeReport',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.reports.delinquencyNoticeReport
          },

          /*
           * {
           *   title: 'watchList',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService',
           *     'AccountingDept'
           *   ],
           *   href: `${screenRoutes.reports.watchList}`
           * },
           */
          {
            title: 'defaultedLoansReport',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.reports.defaultedLoansReport
          },
          {
            title: 'eQLoanListReport',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.reports.eQLoanListReport
          },
          {
            title: 'watchList',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.loanManagementFilters.management.list
          },
          {
            title: 'portfolioBalance',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.reports.portfolioBalance
          },

          /*
           * {
           *   title: 'insuranceReport',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService',
           *     'AccountingDept',
           *   ],
           *   href: `${screenRoutes.reports.insuranceReport}`,
           * },
           */
          {
            title: 'historicalDataTape',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.reports.historicalDataTape
          },
          {
            title: 'loanPerformanceDashboard',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            // href: screenRoutes.reports.loanPerformanceDashboard,
            href: 'https://servicing-portfolio-management.prestti.com/'
          }
        ]
      },

      /*
       *       {
       *         title: 'Request',
       *         roleAccess: ['SuperUser',
       * 'Admin',
       * 'LoanServicer',
       * 'CustomerService'],
       *         icon: FilePlusIcon,
       *         href: `${screenRoutes.loanRequests.list}`,
       *       },
       */
      /*
       * {
       *   title: 'Loans',
       *   roleAccess: [
       *     'SuperUser',
       *     'Admin',
       *     'LoanServicer',
       *     'CustomerService',
       *     'AccountingDept'
       *   ],
       *   icon: CreditCardIcon,
       *   href: `${screenRoutes.loans.list}`
       * },
       */
      {
        title: 'Agents',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: BotIcon,
        items: [
          {
            title: 'Collections Agent',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.collectionsAgent
          },
          {
            title: 'Insurance Compliance Agent',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.collateralReports.insurance.complianceAgent
          }
        ].sort((a, b) => a.title.localeCompare(b.title))
      },
      {
        title: 'riskManagement',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: AssignmentLateOutlinedIcon,
        items: [
          {
            title: 'collateral',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            items: [
              {
                title: 'collateralValuationReport',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService'
                ],
                // icon: AssessmentIcon,
                href: screenRoutes.reports.collateralValuationReport
              },
              {
                title: 'collateralReport',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService'
                ],
                // icon: WorkspacePremiumOutlinedIcon,
                href: screenRoutes.reports.collateralReport
              },
              {
                title: 'gpsMonitoring',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService'
                ],
                // icon: ErrorOutlineOutlinedIcon,
                href: screenRoutes.reports.gpsMonitoring
              }
            ]
          },
          {
            title: 'reports',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.riskManagement.reports
          },
          {
            title: 'filters',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.riskManagement.filters
          }

          /*
           * {
           *   title: 'create',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService'
           *   ],
           *   href: `${screenRoutes.riskManagement.create}`
           * }
           */
        ]
      },

      /* Collateral Reports */
      {
        title: 'collateralReports',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: PollOutlinedIcon,
        items: [
          {
            title: 'insurance',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            // icon: PolicyIcon,
            items: [
              {
                title: 'AI Compliance',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService'
                ],
                href: screenRoutes.collateralReports.insurance.complianceAgent
              },
              {
                title: 'policies',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService',
                  'AccountingDept'
                ],
                href: screenRoutes.collateralReports.insurance.policies
              },
              {
                title: 'financingOffer',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService',
                  'AccountingDept'
                ],
                href: screenRoutes.collateralReports.insurance.financingOffer
              },
              {
                title: 'claims',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService',
                  'AccountingDept'
                ],
                href: screenRoutes.collateralReports.insurance.claims
              },
              {
                title: 'carrier',
                roleAccess: [
                  'SuperUser',
                  'Admin',
                  'LoanServicer',
                  'CustomerService',
                  'AccountingDept'
                ],
                href: screenRoutes.collateralReports.insurance.carrier
              }
            ]
          },
          {
            title: 'driverLicenses',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            // icon: BadgeOutlinedIcon,
            href: screenRoutes.collateralReports.driverLicenses.list
          },
          {
            title: 'vehicles',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            // icon: BadgeOutlinedIcon,
            href: screenRoutes.collateralReports.vehicles.list
          }
        ]
      },
      {
        title: 'customers',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: UsersIcon,
        href: screenRoutes.customers.list
      },
      {
        title: 'contacts',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        icon: ContactsOutlinedIcon,
        href: screenRoutes.contacts.list
      },
      {
        title: 'tasks',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: CalendarIcon,
        href: screenRoutes.tasks.list
      }
    ]
  },

  /*
   *   {
   *     subheader: 'ai',
   *     roleAccess: ['SuperUser',
   * 'Admin',
   * 'AccountingDept'],
   *     items: [
   *       {
   *         title: 'gemini',
   *         roleAccess: ['SuperUser',
   * 'Admin',
   * 'AccountingDept'],
   *         icon: AutoAwesomeIcon,
   *         href: screenRoutes.ai.gemini,
   *       },
   *     ],
   *   },
   */
  {
    subheader: 'accounting',
    roleAccess: ['SuperUser', 'Admin', 'AccountingDept'],
    items: [
      {
        title: 'dashboard',
        roleAccess: ['SuperUser', 'Admin', 'AccountingDept'],
        icon: DashboardIcon,
        href: screenRoutes.accounting.dashboard
      },
      {
        title: 'reports',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        icon: FileTextIcon,
        href: screenRoutes.reports.list,
        items: [

          /*
           * {
           *   title: 'general',
           *   href: `${screenRoutes.reports.general}`
           * },
           */
          {
            title: 'accountsReceivableAging',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.reports.loansAccountReceivableAgingReport
          },

          /*
           * {
           *   title: 'plaidTransactions',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService',
           *     'AccountingDept'
           *   ],
           *   href: `${screenRoutes.reports.plaidTransactions}`
           * },
           */
          {
            title: 'dailyPayments',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: `${screenRoutes.reports.dailyPayments}`
          },

          /*
           * {
           *   title: 'delinquency',
           *   roleAccess: [
           *     'SuperUser',
           *     'Admin',
           *     'LoanServicer',
           *     'CustomerService',
           *     'AccountingDept'
           *   ],
           *   href: `${screenRoutes.reports.delinquency}`
           * },
           */

          {
            title: 'loansReceivable',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.reports.loansReceivableReport
          },
          {
            title: 'portfolioBalance',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService',
              'AccountingDept'
            ],
            href: screenRoutes.reports.portfolioBalance
          }
        ]
      }
    ]
  },
  {
    subheader: 'Settings',
    roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
    items: [

      /*
       * {
       *   title: 'originators',
       *   icon: StoreIcon,
       *   href: '/app/configuration/originators'
       * },
       */

      {
        title: 'Products',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: WorkOutlineOutlinedIcon,
        href: screenRoutes.loanProducts.list
      },
      {
        title: 'access',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: ControlPointIcon,
        href: screenRoutes.access.users.list,
        items: [
          {
            title: 'users',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.access.users.list
            // icon: UserIcon
          }

          /*
           * {
           *   title: 'roles',
           *   href: `${screenRoutes.access.roles}`,
           *   icon: LayerIcon
           * }
           */
        ]
      },
      {
        title: 'loanManagementFilters',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: ClipboardIcon,
        href: screenRoutes.loanManagementFilters.factorRules.list
        // items: [

        //   /*
        //    * {
        //    *   title: 'manage',
        //    *   roleAccess: [
        //    *     'SuperUser',
        //    *     'Admin',
        //    *     'LoanServicer',
        //    *     'CustomerService',
        //    *   ],
        //    *   href: `${screenRoutes.loanManagementFilters.management.list}`,
        //    * },
        //    */
        //   {
        //     title: 'filters',
        //     roleAccess: [
        //       'SuperUser',
        //       'Admin',
        //       'LoanServicer',
        //       'CustomerService',
        //     ],
        //     href: `${screenRoutes.loanManagementFilters.factorRules.list}`,
        //   },
        // ],
      },
      {
        title: 'campaigns',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: MailOutlineOutlinedIcon,
        href: screenRoutes.campaigns.list,
        items: [
          {
            title: 'rules',
            roleAccess: [
              'SuperUser',
              'Admin',
              'LoanServicer',
              'CustomerService'
            ],
            href: screenRoutes.campaigns.list
          }
        ]
      },
      {
        title: 'Organization',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        icon: BusinessIcon,
        href: screenRoutes.organization.settings
      }
    ]
  }
];

function renderNavItems({ items, roles, gpsNotifications, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        // eslint-disable-next-line no-use-before-define
        (acc, item) => reduceChildRoutes({
            acc,
            item,
            roles,
            gpsNotifications,
            ...rest
          }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  roles,
  gpsNotifications,
  depth = 0
}) {
  const key = item.title + depth;
  const hasAccess = item.roleAccess
    ? includesAny(item.roleAccess, roles)
    : false;

  if (!hasAccess) {
    acc.push(<div />);
  } else if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });
    acc.push(
      <NavItem
        key={key}
        depth={depth}
        gpsNotifications={gpsNotifications}
        icon={item.icon}
        info={item.info}
        open={Boolean(open)}
        title={item?.title ?? ''}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          roles,
          gpsNotifications
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        key={key}
        depth={depth}
        gpsNotifications={gpsNotifications}
        href={item.href}
        icon={item.icon}
        info={item.info}
        pathName={pathname}
        title={item?.title ?? ''}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(theme => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
  menuSiteBar: {
    backgroundColor: '#2f4a9c',
    borderRight: '1px solid rgba(255, 255, 255, 0.12)'
  },
  logoColor: {
    backgroundColor: theme.palette.secondary.logo
  },
  logoutButton: {
    color: '#FFF',
    justifyContent: 'flex-start',
    textTransform: 'none'
  }
}));

function NavBar({ openMobile, onMobileClose }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector(state => state.account);
  const gpsNotifications = 0;

  const handleLogout = async () => {
    if (onMobileClose) {
      onMobileClose();
    }
    await dispatch(logout());
    window.location.href = '/';
  };

  /*
   * const getGpsNotifications = useCallback(() => {
   *   loanService
   *     .getGpsNotifications()
   *     .then((response) => {
   *       if (response.gpsNotifications) {
   *         setGpsNotifications(response.gpsNotifications);
   *       }
   *     })
   *     .catch((error) => {
   *       enqueueSnackbar(t(error.message.toString()), {
   *         variant: 'error',
   *       });
   *     });
   * }, []);
   */

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    // getGpsNotifications();
  }, []);

  const content = (
    <Box
      className={classes.menuSiteBar}
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box display="flex" justifyContent="center" />
        </Hidden>
        {/* <Box
          className={classes.logoColor}
          display="flex"
          justifyContent="center"
          p={2}
        >
          <RouterLink to="/app/management/loans">
            <Logo />
          </RouterLink>
        </Box> */}
        <Box p={2}>
          {navConfig.map(config => {
            const hasAccess = config.roleAccess
              ? includesAny(config.roleAccess, user?.roles ?? [])
              : false;

            if (!hasAccess) {
              return <></>;
            }

            return (
              <List
                key={config.subheader}
                subheader={(
                  <ListSubheader
                    disableGutters
                    disableSticky
                    style={{ color: '#FFF' }}
                  >
                    {t(config.subheader)}
                  </ListSubheader>
                )}
              >
                {renderNavItems({
                  items: config.items,
                  pathname: location.pathname,
                  roles: user?.roles ?? [],
                  gpsNotifications
                })}
              </List>
            );
          })}
          <Hidden lgUp>
            <Box mt={2}>
              <Button
                className={classes.logoutButton}
                fullWidth
                onClick={handleLogout}
                startIcon={<ExitToAppIcon />}
              >
                {t('Logout')}
              </Button>
            </Box>
          </Hidden>
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
